import { Fragment, useContext, useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { AuthContext } from "../../provider/AuthProvider";

import { Center } from "../../components/StyledComponents";
import Grid from "../../components/Grid";
import Button from "../../components/Button";
import Form from "../../components/Form";
import Input from "../../components/Form/Input";
import Submit from "../../components/Form/Submit";
import ErrorMessage from "../../components/Form/ErrorMessage";

import {
  schema as signInSchema,
  initialValues as signInInitialValues,
} from "../../constants/form/auth/sign-in";

import {
  schema as recoveryCodeSchema,
  initialValues as recoveryCodeInitialValues,
} from "../../constants/form/auth/recovery-code";

import {
  schema as passwordRecoverySchema,
  initialValues as passwordRecoveryInitialValues,
} from "../../constants/form/auth/password-recovery";

import {
  recoverPassword,
  resetPassword,
  signIn,
} from "../../store/actions/auth";

const ACTIVATION_GENERIC_ERROR_MESSAGE = "Error al solicitar el código de activación";

interface CustomizedState {
  siginStates: {
    loading: boolean;
    success: boolean;
    error: boolean;
  };
  from: {
    pathname: string;
    search: string;
    hash: string;
  };
}

const Page = ({
  userStates,
  authStates,
  userInformation,
  recoverPasswordStates,
  recoverPasswordInformation,
  resetPasswordStates,
}) => {
  let navigate = useNavigate();
  let location = useLocation();
  let { isAuthenticated } = useContext(AuthContext);
  let from = (location.state as CustomizedState)?.from?.pathname || "/";
  const queryParams = new URLSearchParams(location.search);
  const initialEmailFromQuery = queryParams.get("email") || "";
  const initialCodeFromQuery = queryParams.get("code") || "";
  const initialVerificationFromQuery = queryParams.get("verification") || "";

  const dispatch = useDispatch();

  const { loading: userLoading } = userStates;
  const { error: authError, loading: authLoading } = authStates;

  // const [showLogin, setShowLogin] = useState(true);
  const [showActivationRequest, setShowActivationRequest] = useState(true);
  const [showActivationSetup, setShowActivationSetup] = useState(false);
  const [activationEmail, setActivationEmail] = useState(initialEmailFromQuery);
  const [activationVerification, setActivationVerification] = useState(
    initialVerificationFromQuery
  );

  const getErrorMessage = (errorState: any) => {
    if (typeof errorState === "string") {
      return errorState;
    }

    if (errorState?.message && typeof errorState.message === "string") {
      return errorState.message;
    }

    return "";
  };

  const appSignIn = ({ values, actions }) => {
    dispatch(signIn(values));
  };

  const sendRecoveryCode = ({ values, actions }) => {
    dispatch(recoverPassword(values));
    setActivationEmail(values.email);
  };

  const changePassword = ({ values, actions }) => {
    dispatch(
      resetPassword({
        ...values,
        verification:
          activationVerification ||
          recoverPasswordInformation?.result?.verification ||
          values.verification ||
          "",
      })
    );
  };

  useEffect(() => {
    if (recoverPasswordStates.success) {
      const verificationFromRecover =
        recoverPasswordInformation?.result?.verification || "";
      if (verificationFromRecover) {
        setActivationVerification(verificationFromRecover);
      }
      setShowActivationRequest(false);
      setShowActivationSetup(true);
    }
  }, [recoverPasswordStates, recoverPasswordInformation]);

  useEffect(() => {
    if (initialEmailFromQuery || initialCodeFromQuery || initialVerificationFromQuery) {
      setShowActivationRequest(false);
      setShowActivationSetup(true);

      if (initialEmailFromQuery) {
        setActivationEmail(initialEmailFromQuery);
      }
      if (initialVerificationFromQuery) {
        setActivationVerification(initialVerificationFromQuery);
      }
    }
  }, [initialEmailFromQuery, initialCodeFromQuery, initialVerificationFromQuery]);

  useEffect(() => {
    if (resetPasswordStates.success) {
      setShowActivationRequest(true);
      setShowActivationSetup(false);

      navigate("/sign-in", { replace: true });
    }
  }, [resetPasswordStates]);

  const recoverPasswordErrorText = getErrorMessage(recoverPasswordStates.error);
  const activationRequestErrorText = recoverPasswordErrorText
    ? recoverPasswordErrorText.toLowerCase().includes("recuper")
      ? ACTIVATION_GENERIC_ERROR_MESSAGE
      : recoverPasswordErrorText
    : "";

  const resetPasswordErrorText = getErrorMessage(resetPasswordStates.error);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
    return () => { };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!!userInformation) {
      navigate("/dashboard", { replace: true });
    }
    return () => { };
  }, []);

  return (
    <>
      {showActivationRequest && (
        <>
          <div>
            <h3>Activá tu cuenta</h3>
            <p>Ingresá el e-mail con el que te registraste.</p>
          </div>

          <Form
            id="activation-code-form"
            rules={recoveryCodeSchema}
            values={recoveryCodeInitialValues}
            onSubmit={sendRecoveryCode}
          >
            <Input
              type="email"
              name="email"
              placeholder="Ingrese su e-mail"
              options={{ label: "E-mail" }}
            />

            <Center horizontal="center" vertical="center">
              <Submit
                form="activation-code-form"
                isSubmmiting={recoverPasswordStates.loading}
              >
                Continuar
              </Submit>

              <Button
                type="button"
                options={{
                  skin: "primary",
                  size: "lg",
                  type: "outline",
                }}
                style={{ marginLeft: "10px" }}
                onClick={() => {
                  setShowActivationRequest(false);
                  setShowActivationSetup(true);
                }}
              >
                cancelar
              </Button>
            </Center>

            <ErrorMessage visible={Boolean(activationRequestErrorText)}>
              {activationRequestErrorText}
            </ErrorMessage>
          </Form>
        </>
      )}

      {showActivationSetup && (
        <>
          <div>
            <h3>Activá tu cuenta</h3>
            <p>
              Ingresá el código de activación que enviamos a tu correo
              electrónico.
            </p>
          </div>

          <Form
            id="activation-password-form"
            rules={passwordRecoverySchema}
            values={{
              ...passwordRecoveryInitialValues,
              email: activationEmail || initialEmailFromQuery,
              code: initialCodeFromQuery,
              verification: activationVerification,
              activation: 'true',
            }}
            onSubmit={changePassword}
          >
            <input
              type="hidden"
              name="verification"
              value={activationVerification}
              readOnly
            />

            <Input
              type="text"
              readOnly
              name="email"
              options={{ label: "Email" }}
            />

            <Input
              type="text"
              name="code"
              placeholder="xxxxxx"
              options={{ label: "Código de activación" }}
              autoComplete="off"
            />

            <Input
              type="password"
              name="password"
              placeholder="Ingrese su contraseña"
              options={{ label: "Nueva contraseña" }}
              autoComplete="off"
            />

            <Input
              type="password"
              name="confirm-password"
              placeholder="Ingrese su contraseña"
              options={{ label: "Confirmar contraseña" }}
              autoComplete="off"
            />

            <Center horizontal="center" vertical="center">
              <Submit
                form="activation-password-form"
                isSubmmiting={resetPasswordStates.loading}
              >
                Activar cuenta
              </Submit>

              <Button
                type="button"
                options={{
                  skin: "primary",
                  size: "lg",
                  type: "outline",
                }}
                style={{ marginLeft: "10px" }}
                onClick={() => {
                  setShowActivationRequest(true);
                  setShowActivationSetup(false);
                }}
              >
                cancelar
              </Button>
            </Center>

            <ErrorMessage visible={Boolean(resetPasswordErrorText)}>
              {resetPasswordErrorText}
            </ErrorMessage>
          </Form>
        </>
      )}
    </>
  );
};

const state = ({ authStore, userStore }) => {
  const { signin, recoverPassword, resetPassword } = authStore;
  const { states: authStates } = signin;
  const { states: recoverPasswordStates, data: recoverPasswordInformation } =
    recoverPassword;
  const { states: resetPasswordStates } = resetPassword;
  const { data: userInformation, states: userStates } = userStore.information;
  return {
    authStates,
    recoverPasswordStates,
    recoverPasswordInformation,
    resetPasswordStates,
    userStates,
    userInformation,
  };
};

export default connect(state)(Page);
